import { useEffect, useState } from 'react'
import {
  Camera, Check, Copy, ExternalLink, MapPin, RefreshCw, ShieldOff, Nfc } from 'lucide-react'
import { Shell, useShellMeta } from '../admin/Shell.js'
import { Link } from '../router.js'
import { ToastProvider, useToast, apiGet, adminMutate, usePermissions } from '../admin/primitives.js'
import { Field, TextInput } from '../admin/form.js'
import { ConfirmDialog } from '../auth/Dialog.js'
import { useIntegrationCatalog, IntegrationStatusPill } from '../navigation/integration-status.js'
import {
  HEALTH_FR, estErreurApi, fraicheurFr,
  type CapabilityLine, type DiagnosticStep,
} from './lib.js'

/**
 * INTÉGRATIONS COMMUNICATION — de vraies pages, aucun drawer (§1).
 *
 * Le tutoriel n'est pas un texte figé : chaque étape est un FAIT constaté
 * par le serveur (§61) — configuration présente, appel réel réussi,
 * permission refusée par le fournisseur. Les liens de documentation sont
 * OFFICIELS et datés (§63) ; aucune capture d'écran des consoles Meta ou
 * Google, qui changent plus vite que nous.
 */

/* ─────────────────────────── CopyField (§257) ─────────────────────────── */

/** Valeur CANONIQUE venue du serveur — jamais reconstruite du navigateur. */
export function CopyField({ label, value, helper }: { label: string; value: string; helper?: string }) {
  const [copie, setCopie] = useState(false)
  return (
    <Field label={label} helper={helper}>
      <div className="cm-copyfield">
        <code>{value}</code>
        <button type="button" className="lc-btn" onClick={() => {
          void navigator.clipboard.writeText(value).then(() => {
            setCopie(true); setTimeout(() => setCopie(false), 1800)
          })
        }}>
          {copie ? <Check size={14} aria-hidden="true" /> : <Copy size={14} aria-hidden="true" />}
          {copie ? 'Copié' : 'Copier'}
        </button>
      </div>
    </Field>
  )
}

/* ─────────────────────────── briques du diagnostic ────────────────────── */

function Stepper({ steps }: { steps: DiagnosticStep[] }) {
  return (
    <ol className="cm-steps">
      {steps.map((s, i) => (
        <li key={s.key} className={s.done ? 'cm-steps__item cm-steps__item--done' : 'cm-steps__item'}>
          <span className="cm-steps__dot" aria-hidden="true">{s.done ? <Check size={13} /> : i + 1}</span>
          <div>
            <strong>{s.title}</strong>
            {s.detail ? <p className="lc-hint">{s.detail}</p> : null}
          </div>
        </li>
      ))}
    </ol>
  )
}

function pillCapacite(state: string, label: string) {
  const cls = state === 'AVAILABLE' ? 'lc-pill--ok'
    : state === 'BLOCKED_SAFE_MODE' ? 'lc-pill--warning'
    : state === 'PERMISSION_MISSING' ? 'lc-pill--warning'
    : 'lc-pill--idle'
  return <span className={`lc-pill ${cls}`}>{label}</span>
}

/** MATRICE DES CAPACITÉS (§118) — « permission manquante » est un fait,
 *  pas une panne : la ligne le dit, sans alarmisme. */
function MatriceCapacites({ items }: { items: CapabilityLine[] }) {
  return (
    <table className="cm-captable">
      <thead><tr><th>Capacité</th><th>État</th></tr></thead>
      <tbody>
        {items.map((c) => (
          <tr key={c.capability}>
            <td>{c.label}</td>
            <td>{pillCapacite(c.state, c.state_label)}{c.detail ? <span className="lc-hint cm-captable__hint">{c.detail}</span> : null}</td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

function LiensDocs({ docs, verifiedOn }: { docs: Record<string, string>; verifiedOn: string }) {
  const LIBELLES: Record<string, string> = {
    appDashboard: 'Tableau de bord des apps Meta', instagramPlatform: 'Plateforme Instagram',
    instagramApiWithLogin: 'API Instagram (connexion Facebook)', contentPublishing: 'Publication de contenu',
    insights: 'Statistiques (Insights)', appReview: 'Validation Meta (App Review)',
    accessTokens: 'Jetons longue durée',
    cloudConsole: 'Console Google Cloud', businessProfileApis: 'APIs Business Profile',
    accessRequest: 'Demande d’accès à l’API', performanceApi: 'API Performance',
    reviews: 'Données des avis', localPosts: 'Posts de la fiche',
    oauthConsent: 'Écran de consentement OAuth', credentials: 'Identifiants OAuth',
  }
  return (
    <section className="lc-panel">
      <header className="lc-panel__head"><h2 className="lc-section-title">Documentation officielle</h2>
        <span className="lc-hint">liens vérifiés le {new Date(verifiedOn).toLocaleDateString('fr-FR')}</span>
      </header>
      <div className="lc-panel__body cm-doclinks">
        {Object.entries(docs).map(([k, url]) => (
          <a key={k} href={url} target="_blank" rel="noreferrer" className="cm-doclinks__item">
            <ExternalLink size={13} aria-hidden="true" /> {LIBELLES[k] ?? k}
          </a>
        ))}
      </div>
    </section>
  )
}

function BoutonSync({ provider, onDone }: { provider: 'instagram' | 'google-business'; onDone: () => void }) {
  const toast = useToast()
  const [busy, setBusy] = useState(false)
  const permissions = usePermissions()
  if (!permissions.permissions.includes('communication.settings.manage')) return null
  return (
    <button type="button" className="lc-btn" disabled={busy} onClick={async () => {
      setBusy(true)
      const r = await adminMutate<{ error?: string; message?: string; ok?: boolean; detail?: string }>(
        'POST', `/api/v1/communication/integrations/${provider}/sync`)
      setBusy(false)
      if (r.status === 200 && r.data.ok !== false) toast('ok', r.data.detail ?? 'Synchronisation terminée.')
      else toast('error', r.data.detail ?? r.data.message ?? 'Synchronisation impossible.')
      onDone()
    }}>
      <RefreshCw size={14} aria-hidden="true" className={busy ? 'cm-spin' : ''} />
      {busy ? 'Synchronisation…' : 'Synchroniser maintenant'}
    </button>
  )
}

/* ═══════════════════════ HUB /communication/integrations ═══════════════ */

interface HubData {
  safe_mode: boolean
  docs_verified_on: string
  instagram: { connected: boolean; health: string; username: string | null; last_sync_at: string | null }
  google_business: { connected: boolean; health: string; location: string | null; last_sync_at: string | null }
}

function pillSante(h: string) {
  if (h === 'OPERATIONAL') return <span className="lc-pill lc-pill--ok"><span className="lc-dot lc-dot--ok lc-dot--live" />{HEALTH_FR[h]}</span>
  if (h === 'NOT_CONFIGURED') return <span className="lc-pill lc-pill--idle">{HEALTH_FR[h]}</span>
  return <span className="lc-pill lc-pill--warning">{HEALTH_FR[h] ?? h}</span>
}

export function CommIntegrationsHubPage() {
  const meta = useShellMeta()
  // IA V2 : l'ÉTAT vient du catalogue canonique — le même que Réglages →
  // Intégrations (§93, §156). Les infos métier (compte, fiche) restent ici.
  const catalogue = useIntegrationCatalog()
  const canon = (id: string) => catalogue?.find((i) => i.id === id) ?? null
  const pill = (id: string, replis: string) => {
    const c = canon(id)
    return c ? <IntegrationStatusPill status={c.status} label={c.status_label} /> : pillSante(replis)
  }
  const [data, setData] = useState<HubData | null>(null)
  const [erreur, setErreur] = useState<string | null>(null)
  useEffect(() => {
    void apiGet<HubData | { error: string; message?: string }>('/api/v1/communication/integrations')
      .then((j) => { if (estErreurApi(j)) setErreur(j.message ?? 'Lecture impossible.'); else setData(j) })
      .catch(() => setErreur('Gestion n’a pas répondu.'))
  }, [])

  return (
    <Shell active="communication" meta={meta}>
      <main className="lc-page lc-stagger cm-page">
        <Link className="lc-crumb" href="/settings/integrations">← Intégrations</Link>
        <header className="cm-head">
          <div>
            <h1 className="lc-title">Intégrations</h1>
            <p className="lc-subtitle">Les canaux que Gestion lit — et, portail ouvert, sur lesquels il publiera.</p>
          </div>
        </header>
        {data?.safe_mode ? (
          <p className="cm-safenote"><ShieldOff size={14} aria-hidden="true" />
            Mode sûr actif : les lectures fonctionnent normalement, les écritures externes sont retenues.</p>
        ) : null}
        {erreur ? <p className="lc-hint">{erreur}</p> : null}
        <div className="cm-intgrid">
          <Link href="/communication/integrations/instagram" className="lc-card lc-card--violet cm-intcard">
            <span className="lc-card__edge" aria-hidden="true" />
            <span className="lc-card__icon" aria-hidden="true"><Camera /></span>
            <div>
              <h2>Instagram</h2>
              <p className="lc-hint">
                {data?.instagram.username ? `@${data.instagram.username} · ` : ''}
                {data ? `synchronisé ${fraicheurFr(data.instagram.last_sync_at)}` : '…'}
              </p>
            </div>
            {data ? pill('instagram_communication', data.instagram.health) : null}
          </Link>
          <Link href="/communication/integrations/tracker" className="lc-card lc-card--cyan cm-intcard">
            <span className="lc-card__edge" aria-hidden="true" />
            <span className="lc-card__icon" aria-hidden="true"><Nfc /></span>
            <div>
              <h2>Tracker Club</h2>
              <p className="lc-hint">
                insta.liveclubrennes.com
                {canon('instagram_tracker')?.last_sync_at ? ` · synchronisé ${fraicheurFr(canon('instagram_tracker')!.last_sync_at)}` : ''}
              </p>
              {canon('instagram_tracker')?.summary ? <p className="lc-hint">{canon('instagram_tracker')!.summary}</p> : null}
              {canon('instagram_tracker') ? (
                <p className="lc-hint">
                  {canon('instagram_tracker')!.capabilities.map((c) => `${c.label} ${c.active ? '✓' : '—'}`).join(' · ')}
                </p>
              ) : null}
            </div>
            {canon('instagram_tracker') ? <IntegrationStatusPill status={canon('instagram_tracker')!.status} label={canon('instagram_tracker')!.status_label} /> : null}
          </Link>
          <Link href="/communication/integrations/google" className="lc-card lc-card--cyan cm-intcard">
            <span className="lc-card__edge" aria-hidden="true" />
            <span className="lc-card__icon" aria-hidden="true"><MapPin /></span>
            <div>
              <h2>Google Business Profile</h2>
              <p className="lc-hint">
                {data?.google_business.location ? `${data.google_business.location} · ` : ''}
                {data ? `synchronisé ${fraicheurFr(data.google_business.last_sync_at)}` : '…'}
              </p>
            </div>
            {data ? pill('google_business', data.google_business.health) : null}
          </Link>
        </div>
        <p className="lc-hint cm-roadnote">
          Facebook, TikTok et WhatsApp pourront rejoindre cette page — l’architecture
          par capacités les attend, aucun n’est branché aujourd’hui.
        </p>
      </main>
    </Shell>
  )
}


/* ═══════════ INSTAGRAM OWNER — connexion propriétaire (V1.1) ═══════════ */

interface OwnerInfo {
  state: 'NOT_CONFIGURED' | 'NEEDS_AUTH' | 'CONNECTED' | 'RECONNECT' | 'ACCOUNT_MISMATCH'
  app_id: string | null
  graph_version: string
  username: string | null
  name: string | null
  account_type: string | null
  connected_at: string | null
  token_days_left: number | null
  last_sync_at: string | null
  last_error: string | null
  pending: { username: string; name: string | null } | null
  /** Le compte du club (réglage) auquel le serveur compare l'autorisation. */
  expected_username: string | null
  scopes: string[]
  redirect_uri: string
  capabilities: { capability: string; label: string; state: string; detail: string | null }[]
  docs: Record<string, string>
  docs_verified_on: string
}

const OWNER_CAP_STATE_FR: Record<string, string> = {
  AVAILABLE: 'Disponible', UNAVAILABLE: 'Non disponible',
  EMPTY_PROVIDER_DATA: 'Aucune donnée renvoyée', NOT_CONNECTED: 'À relever',
  NOT_SUPPORTED: 'Non proposé par l’API', NOT_REQUESTED: 'Non demandé',
}

/** « @compte » du club tel que RÉGLÉ dans Gestion — jamais un nom écrit en
 *  dur : le compte officiel a déjà changé une fois. Réglage vide → rien. */
function suffixeCompte(owner: OwnerInfo): string {
  return owner.expected_username ? ` @${owner.expected_username}` : ''
}

function TutoOwner({ owner }: { owner: OwnerInfo }) {
  return (
    <section className="lc-panel">
      <header className="lc-panel__head"><h2 className="lc-section-title">Connecter le compte du club — pas à pas</h2></header>
      <div className="lc-panel__body">
        <p className="lc-hint ig-avant">
          <strong>Avant de commencer :</strong> un compte Instagram professionnel
          (Business ou Creator) · l’accès au compte du club{suffixeCompte(owner)} · une application
          Meta · le rôle administrateur sur cette application.
        </p>
        <ol className="cm-steps">
          <li className={owner.app_id ? 'cm-steps__item cm-steps__item--done' : 'cm-steps__item'}>
            <span className="cm-steps__dot" aria-hidden="true">{owner.app_id ? <Check size={13} /> : 1}</span>
            <div>
              <strong>Préparer l’application Meta</strong>
              <p className="lc-hint">
                Ouvrez <a href={owner.docs['appDashboard']} target="_blank" rel="noreferrer">Meta for Developers</a> →
                votre application → produit « Instagram » → « API setup with
                Instagram login ». Notez l’<em>Instagram App ID</em> et
                l’<em>Instagram App Secret</em> qui y figurent.
              </p>
            </div>
          </li>
          <li className={owner.app_id ? 'cm-steps__item cm-steps__item--done' : 'cm-steps__item'}>
            <span className="cm-steps__dot" aria-hidden="true">{owner.app_id ? <Check size={13} /> : 2}</span>
            <div>
              <strong>Configurer l’URL de redirection</strong>
              <p className="lc-hint">Dans « Business login settings » → « OAuth redirect URIs », collez exactement l’adresse ci-dessous.</p>
            </div>
          </li>
          <li className="cm-steps__item">
            <span className="cm-steps__dot" aria-hidden="true">3</span>
            <div>
              <strong>Vérifier les permissions</strong>
              <p className="lc-hint">
                Le flow ne demande QUE {owner.scopes.join(' et ')} — lecture et
                statistiques, rien d’autre (moindre privilège). Pour analyser
                son PROPRE compte, aucune validation App Review n’est requise :
                le compte qui autorise doit simplement avoir un rôle sur
                l’application (testeur Instagram accepté via
                instagram.com/accounts/manage_access).
              </p>
            </div>
          </li>
          <li className={owner.state !== 'NOT_CONFIGURED' ? 'cm-steps__item cm-steps__item--done' : 'cm-steps__item'}>
            <span className="cm-steps__dot" aria-hidden="true">{owner.state !== 'NOT_CONFIGURED' ? <Check size={13} /> : 4}</span>
            <div><strong>Enregistrer les identifiants ci-dessous</strong>
              <p className="lc-hint">Le secret part au coffre chiffré — jamais relu, jamais journalisé.</p></div>
          </li>
          <li className={['CONNECTED', 'RECONNECT'].includes(owner.state) ? 'cm-steps__item cm-steps__item--done' : 'cm-steps__item'}>
            <span className="cm-steps__dot" aria-hidden="true">{['CONNECTED', 'RECONNECT'].includes(owner.state) ? <Check size={13} /> : 5}</span>
            <div><strong>Connecter le compte du club{suffixeCompte(owner)}</strong>
              <p className="lc-hint">C’est vous qui vous identifiez chez Instagram — Gestion ne voit jamais le mot de passe.</p></div>
          </li>
          <li className={owner.last_sync_at ? 'cm-steps__item cm-steps__item--done' : 'cm-steps__item'}>
            <span className="cm-steps__dot" aria-hidden="true">{owner.last_sync_at ? <Check size={13} /> : 6}</span>
            <div><strong>Tester puis synchroniser</strong>
              <p className="lc-hint">« Tester la connexion » sonde chaque capacité réelle ; la première synchronisation démarre l’historique.</p></div>
          </li>
        </ol>
        <CopyField label="URL de redirection à déclarer chez Meta" value={owner.redirect_uri}
          helper="Valeur canonique fournie par le serveur — à coller telle quelle." />
        <p className="lc-hint">
          Documentation officielle : <a href={owner.docs['instagramLogin']} target="_blank" rel="noreferrer">Business Login</a> ·{' '}
          <a href={owner.docs['userInsights']} target="_blank" rel="noreferrer">Insights compte</a> ·{' '}
          <a href={owner.docs['mediaInsights']} target="_blank" rel="noreferrer">Insights média</a> —
          vérifiée le {new Date(owner.docs_verified_on).toLocaleDateString('fr-FR')}.
        </p>
      </div>
    </section>
  )
}

function OwnerAppForm({ onSaved }: { onSaved: () => void }) {
  const toast = useToast()
  const [appId, setAppId] = useState('')
  const [secret, setSecret] = useState('')
  const [busy, setBusy] = useState(false)
  const permissions = usePermissions()
  if (!permissions.permissions.includes('communication.settings.manage')) {
    return <p className="lc-hint">La configuration demande le droit « gérer les réglages communication ».</p>
  }
  return (
    <div className="cm-credform">
      <Field label="Instagram App ID" helper="Visible dans le produit Instagram de l’application Meta.">
        <input className="lc-input" value={appId} placeholder="123456789012345"
          autoComplete="off" name="ig_app_identifier" inputMode="numeric"
          onChange={(e) => setAppId(e.target.value)} aria-label="Instagram App ID" />
      </Field>
      <Field label="Instagram App Secret" helper="Posé au coffre chiffré — jamais relu, jamais journalisé.">
        <input className="lc-input" type="password" value={secret} autoComplete="new-password"
          name="ig_app_secret" onChange={(e) => setSecret(e.target.value)} aria-label="Instagram App Secret" />
      </Field>
      <button type="button" className="lc-btn lc-btn--primary" disabled={busy || !appId.trim() || !secret.trim()}
        onClick={async () => {
          setBusy(true)
          const r = await adminMutate<{ error?: string; message?: string }>(
            'PUT', '/api/v1/communication/integrations/instagram/owner-app',
            { app_id: appId.trim(), app_secret: secret.trim() })
          setBusy(false)
          if (r.status === 200) { toast('ok', 'Identifiants enregistrés au coffre.'); setSecret(''); onSaved() }
          else toast('error', r.data.message ?? 'Enregistrement impossible.')
        }}>
        Enregistrer au coffre
      </button>
    </div>
  )
}

function OwnerTestPanel() {
  const [resultat, setResultat] = useState<{ ok: boolean; checks: { key: string; label: string; ok: boolean | null; detail: string | null }[] } | null>(null)
  const [busy, setBusy] = useState(false)
  return (
    <div>
      <button type="button" className="lc-btn" disabled={busy} onClick={async () => {
        setBusy(true)
        const r = await adminMutate<{ error?: string; ok?: boolean; checks?: { key: string; label: string; ok: boolean | null; detail: string | null }[] }>(
          'POST', '/api/v1/communication/integrations/instagram/owner/test')
        setBusy(false)
        if (r.data.checks) setResultat({ ok: r.data.ok ?? false, checks: r.data.checks })
      }}>
        <RefreshCw size={14} aria-hidden="true" className={busy ? 'cm-spin' : ''} />
        {busy ? 'Sondes en cours…' : 'Tester la connexion'}
      </button>
      {resultat ? (
        <ul className="cm-actionlist" style={{ marginTop: 12 }}>
          {resultat.checks.map((c) => (
            <li key={c.key}>
              <span className="cm-actionlist__label">{c.label}</span>
              {c.ok === true ? <span className="lc-pill lc-pill--ok">✓</span>
                : c.ok === false ? <span className="lc-pill lc-pill--warning">✗</span>
                : <span className="lc-pill lc-pill--idle">?</span>}
              {c.detail ? <span className="lc-hint">{c.detail}</span> : null}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  )
}

function OwnerSection({ onChanged, version }: { onChanged: () => void; version: number }) {
  const toast = useToast()
  const [owner, setOwner] = useState<OwnerInfo | null>(null)
  const [busy, setBusy] = useState(false)
  const [confirmerDeco, setConfirmerDeco] = useState(false)
  const [confirmerAdoption, setConfirmerAdoption] = useState(false)
  const permissions = usePermissions()
  const peutGerer = permissions.permissions.includes('communication.settings.manage')

  const charger = () => {
    void apiGet<OwnerInfo | { error: string; message?: string }>(
      '/api/v1/communication/integrations/instagram/owner')
      .then((j) => { if (!estErreurApi(j)) setOwner(j) })
      .catch(() => undefined)
  }
  useEffect(charger, [version])
  if (!owner) return null
  // Le compte attendu vient du serveur (réglage « compte du club »), celui
  // auquel il compare l'autorisation — jamais un nom écrit en dur ici.
  const attendu = owner.expected_username ? `@${owner.expected_username}` : 'compte du club non renseigné'

  const connecter = async () => {
    setBusy(true)
    const r = await adminMutate<{ error?: string; url?: string; message?: string }>(
      'POST', '/api/v1/communication/integrations/instagram/owner/oauth/start')
    setBusy(false)
    if (r.status === 200 && r.data.url) window.location.href = r.data.url
    else toast('error', r.data.message ?? 'Impossible de démarrer la connexion.')
  }

  return (
    <>
      <section className="lc-panel">
        <header className="lc-panel__head">
          <h2 className="lc-section-title">Connexion propriétaire — Insights</h2>
          {owner.state === 'CONNECTED'
            ? <span className="lc-pill lc-pill--ok"><span className="lc-dot lc-dot--ok lc-dot--live" />Insights connectés</span>
            : owner.state === 'RECONNECT'
              ? <span className="lc-pill lc-pill--warning">Connexion à renouveler</span>
              : <span className="lc-pill lc-pill--idle">Non connectée</span>}
        </header>
        <div className="lc-panel__body">
          {owner.state === 'ACCOUNT_MISMATCH' && owner.pending ? (
            <div className="ig-mismatch">
              <p>
                <strong>Compte autorisé :</strong> @{owner.pending.username}{owner.pending.name ? ` (${owner.pending.name})` : ''}<br />
                <strong>Compte attendu :</strong> {attendu}
              </p>
              <p className="lc-hint">Rien n’a été modifié — à vous de trancher.</p>
              {peutGerer ? (
                <div className="cm-replyactions">
                  <button type="button" className="lc-btn lc-btn--primary" disabled={busy} onClick={async () => {
                    setBusy(true)
                    await adminMutate('POST', '/api/v1/communication/integrations/instagram/owner/reject-account')
                    setBusy(false)
                    void connecter()
                  }}>Reconnecter avec le bon compte</button>
                  {/* Adopter un autre compte remplace le compte du club : jamais
                      en un clic, la confirmation nomme la conséquence. */}
                  <button type="button" className="lc-btn" disabled={busy} onClick={() => setConfirmerAdoption(true)}>Utiliser ce compte</button>
                </div>
              ) : null}
            </div>
          ) : owner.state === 'ACCOUNT_MISMATCH' ? (
            /* Garde serveur : le jeton en place appartient à un AUTRE compte que
               le compte du club — rien n'est relevé avec lui tant que le bon
               compte n'est pas reconnecté. La cause précise vient du serveur
               (`last_error`) : un autre compte, ou le compte du club RENOMMÉ
               (même identifiant) — là, c'est le réglage qu'il faut suivre. */
            <div className="ig-mismatch">
              <p>
                <strong>Compte connecté :</strong> @{owner.username}<br />
                <strong>Compte attendu :</strong> {attendu}
              </p>
              {owner.last_error ? <p className="lc-hint">Dernière erreur : {owner.last_error}</p> : null}
              <p className="lc-hint">Aucune statistique n’est relevée avec ce compte. La lecture publique du compte du club continue.</p>
              {peutGerer ? (
                <div className="cm-replyactions">
                  <button type="button" className="lc-btn lc-btn--primary" disabled={busy} onClick={() => void connecter()}>
                    Reconnecter avec le bon compte <ExternalLink size={14} aria-hidden="true" />
                  </button>
                  {/* Si la reconnexion n'aboutit pas, le jeton de l'autre compte
                      ne doit pas rester au coffre faute de bouton. */}
                  <button type="button" className="lc-btn lc-btn--danger" onClick={() => setConfirmerDeco(true)}>
                    Déconnecter le compte
                  </button>
                </div>
              ) : null}
            </div>
          ) : owner.state === 'CONNECTED' || owner.state === 'RECONNECT' ? (
            <>
              <p className="lc-hint">
                <strong>@{owner.username}</strong>{owner.name ? ` — ${owner.name}` : ''} ·
                type constaté : {owner.account_type ?? 'inconnu'} ·
                jeton {owner.state === 'RECONNECT' ? 'EXPIRÉ' : owner.token_days_left !== null && owner.token_days_left <= 10 ? `renouvellement à venir (${owner.token_days_left} j)` : `valide (${owner.token_days_left} j)`} ·
                connecté {fraicheurFr(owner.connected_at)} ·
                dernière synchro {fraicheurFr(owner.last_sync_at)}
              </p>
              {owner.last_error ? <p className="lc-hint">Dernière erreur : {owner.last_error}</p> : null}
              {owner.state === 'RECONNECT' && peutGerer ? (
                <button type="button" className="lc-btn lc-btn--primary" disabled={busy} onClick={() => void connecter()}>
                  Reconnecter <ExternalLink size={14} aria-hidden="true" />
                </button>
              ) : null}
              {/* V1.3 : un scope manquant (commentaires) ne casse rien — les
                  Insights restent opérationnels, on propose la RÉAUTORISATION. */}
              {owner.state === 'CONNECTED' && peutGerer
                && owner.capabilities.some((c) => c.capability === 'COMMENTS_READ'
                  && c.state === 'UNAVAILABLE' && (c.detail ?? '').includes('Reconnectez')) ? (
                <p className="cm-safenote">
                  Reconnectez Instagram pour autoriser la lecture des commentaires
                  (instagram_business_manage_comments) — les Insights restent opérationnels.
                  <button type="button" className="lc-btn lc-btn--primary" style={{ marginLeft: 12 }}
                    disabled={busy} onClick={() => void connecter()}>
                    Réautoriser Instagram <ExternalLink size={13} aria-hidden="true" />
                  </button>
                </p>
              ) : null}
              <OwnerTestPanel />
              <table className="cm-captable" style={{ marginTop: 14 }}>
                <thead><tr><th>Capacité</th><th>État constaté</th></tr></thead>
                <tbody>
                  {owner.capabilities.map((c) => (
                    <tr key={c.capability}>
                      <td>{c.label}</td>
                      <td>
                        <span className={`lc-pill ${c.state === 'AVAILABLE' ? 'lc-pill--ok' : c.state === 'UNAVAILABLE' ? 'lc-pill--warning' : 'lc-pill--idle'}`}>
                          {OWNER_CAP_STATE_FR[c.state] ?? c.state}
                        </span>
                        {c.detail ? <span className="lc-hint cm-captable__hint">{c.detail}</span> : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {peutGerer ? (
                <div className="cm-replyactions" style={{ marginTop: 14 }}>
                  <button type="button" className="lc-btn lc-btn--danger" onClick={() => setConfirmerDeco(true)}>
                    Déconnecter le compte
                  </button>
                </div>
              ) : null}
            </>
          ) : owner.state === 'NEEDS_AUTH' ? (
            <>
              {/* Sans compte du club réglé, la lecture publique ne tourne pas
                  (« Compte du club non renseigné ») et la garde de compte n'a
                  rien à comparer : l'écran ne promet ni l'une ni l'autre. */}
              {owner.expected_username ? (
                <p className="lc-hint">
                  Le compte du club{suffixeCompte(owner)} est actuellement visible via une
                  lecture publique limitée. Connectez le compte pour accéder aux statistiques
                  détaillées : portée, vues, interactions, contenus, Stories.
                </p>
              ) : (
                <p className="lc-hint">
                  Renseignez d’abord le compte du club ci-dessous : sans lui, aucune lecture
                  publique, et la connexion propriétaire ne pourra pas vérifier le compte autorisé.
                </p>
              )}
              {peutGerer ? (
                <button type="button" className="lc-btn lc-btn--primary" disabled={busy} onClick={() => void connecter()}>
                  Connecter Instagram <ExternalLink size={14} aria-hidden="true" />
                </button>
              ) : null}
            </>
          ) : (
            <OwnerAppForm onSaved={charger} />
          )}
        </div>
      </section>
      {owner.state === 'NOT_CONFIGURED' || owner.state === 'NEEDS_AUTH' ? <TutoOwner owner={owner} /> : null}

      <ConfirmDialog open={confirmerDeco} danger title="Déconnecter le compte Instagram ?"
        confirmLabel="Déconnecter" cancelLabel="Garder la connexion"
        onClose={() => setConfirmerDeco(false)}
        onConfirm={async () => {
          setConfirmerDeco(false)
          const r = await adminMutate<{ error?: string; ok?: boolean; message?: string }>(
            'POST', '/api/v1/communication/integrations/instagram/owner/disconnect')
          if (r.status === 200) { toast('ok', 'Compte déconnecté — historique conservé.'); charger(); onChanged() }
          else toast('error', r.data.message ?? 'Déconnexion impossible.')
        }}>
        Les synchronisations Insights s’arrêtent. L’historique Gestion est
        conservé, et la veille concurrentielle (Business Discovery) n’est pas
        affectée.
      </ConfirmDialog>

      <ConfirmDialog open={confirmerAdoption} danger title="Remplacer le compte du club ?"
        confirmLabel="Utiliser ce compte" cancelLabel="Ne rien changer"
        onClose={() => setConfirmerAdoption(false)}
        onConfirm={async () => {
          setConfirmerAdoption(false)
          setBusy(true)
          const r = await adminMutate<{ error?: string; ok?: boolean; message?: string }>(
            'POST', '/api/v1/communication/integrations/instagram/owner/confirm-account')
          setBusy(false)
          if (r.status === 200 && r.data.ok) { toast('ok', 'Compte adopté.'); charger(); onChanged() }
          else toast('error', r.data.message ?? 'Impossible.')
        }}>
        Le compte du club deviendra @{owner.pending?.username} au lieu de {attendu} ;
        ses statistiques s’écriront dans l’historique du club.
      </ConfirmDialog>
    </>
  )
}

/* ═══════════════════ /communication/integrations/instagram ═════════════ */

interface IgDiag {
  configured: boolean
  club_account: { club_username: string | null; club_name: string | null; verified_at: string | null }
  account_state: string
  account_detail: string | null
  docs: Record<string, string>
  docs_verified_on: string
  capabilities: CapabilityLine[]
  steps: DiagnosticStep[]
}

/**
 * LE COMPTE DU CLUB — réglage vérifié par un appel réel (business_discovery).
 * Le compte technique qui interroge Meta n'est PAS le compte du club ; sans
 * ce réglage, le cockpit ne relève rien plutôt que d'afficher les zéros
 * (exacts) du mauvais compte.
 */
function ClubAccountPanel({ club, onSaved }: {
  club: IgDiag['club_account']; onSaved: () => void
}) {
  const toast = useToast()
  const permissions = usePermissions()
  const peutGerer = permissions.permissions.includes('communication.settings.manage')
  const [saisie, setSaisie] = useState('')
  const [busy, setBusy] = useState(false)
  const [constat, setConstat] = useState<string | null>(null)

  return (
    <section className="lc-panel">
      <header className="lc-panel__head"><h2 className="lc-section-title">Compte du club</h2></header>
      <div className="lc-panel__body cm-credform">
        {club.club_username ? (
          <p className="lc-hint">
            Compte suivi : <strong>@{club.club_username}</strong>
            {club.club_name ? ` — ${club.club_name}` : ''}, vérifié {fraicheurFr(club.verified_at)} par un appel réel.
          </p>
        ) : (
          <p className="lc-hint">
            Aucun compte renseigné : le cockpit ne relève rien tant que le compte
            Instagram du club n’est pas identifié — on n’affiche pas les chiffres
            du compte technique de connexion.
          </p>
        )}
        {peutGerer ? (
          <>
            <Field label={club.club_username ? 'Changer de compte' : 'Nom d’utilisateur Instagram du club'}
              helper="Vérifié auprès de Meta avant enregistrement — un compte qui ne répond pas n’est jamais retenu.">
              <TextInput value={saisie} onChange={setSaisie} placeholder="@liveclubrennes" />
            </Field>
            <button type="button" className="lc-btn lc-btn--primary" disabled={busy || saisie.trim() === ''}
              onClick={async () => {
                setBusy(true); setConstat(null)
                const r = await adminMutate<{ error?: string; ok?: boolean; message?: string
                  account?: { username: string; name: string | null; followers: number; media_count: number } }>(
                  'PUT', '/api/v1/communication/integrations/instagram/account', { username: saisie })
                setBusy(false)
                if (r.status === 200 && r.data.account) {
                  const a = r.data.account
                  setConstat(`Meta confirme @${a.username}${a.name ? ` (${a.name})` : ''} : ${a.followers.toLocaleString('fr-FR')} abonnés, ${a.media_count} publications.`)
                  setSaisie('')
                  toast('ok', 'Compte du club vérifié et enregistré.')
                  onSaved()
                } else {
                  toast('error', r.data.message ?? 'Vérification impossible.')
                }
              }}>
              Vérifier et enregistrer
            </button>
            {constat ? <p className="lc-hint">{constat}</p> : null}
          </>
        ) : null}
      </div>
    </section>
  )
}

function InstagramIntegrationContent() {
  const [diag, setDiag] = useState<IgDiag | null>(null)
  const [erreur, setErreur] = useState<string | null>(null)
  const [verdictOAuth] = useState(() => verdictOAuthDepuisUrl(OAUTH_RETOURS_INSTAGRAM))
  // Changer le compte du club change ce que la connexion propriétaire attend :
  // son bloc est relu, pas laissé sur l'ancien compte jusqu'au F5.
  const [versionOwner, setVersionOwner] = useState(0)
  const charger = () => {
    void apiGet<IgDiag | { error: string; message?: string }>(
      '/api/v1/communication/integrations/instagram/diagnostic')
      .then((j) => { if (estErreurApi(j)) setErreur(j.message ?? 'Lecture impossible.'); else { setDiag(j); setErreur(null) } })
      .catch(() => setErreur('Gestion n’a pas répondu.'))
  }
  useEffect(charger, [])

  return (
    <main className="lc-page lc-stagger cm-page">
      <Link className="lc-crumb" href="/settings/integrations">← Intégrations</Link>
      <header className="cm-head">
        <div>
          <h1 className="lc-title">Instagram</h1>
          <p className="lc-subtitle">Compte professionnel du club — lecture aujourd’hui, publication quand Meta l’autorisera.</p>
        </div>
        <div className="cm-head__actions">
          <BoutonSync provider="instagram" onDone={charger} />
        </div>
      </header>

      {verdictOAuth ? (
        <p className={verdictOAuth.ok ? 'cm-safenote cm-safenote--ok' : 'cm-safenote'} role="status">
          {verdictOAuth.texte}
        </p>
      ) : null}
      {erreur ? <p className="lc-hint">{erreur}</p> : null}
      <OwnerSection onChanged={charger} version={versionOwner} />
      {diag ? (
        <>
          <section className="lc-panel">
            <header className="lc-panel__head"><h2 className="lc-section-title">Lecture publique (veille) — où en est la connexion</h2></header>
            <div className="lc-panel__body">
              <Stepper steps={diag.steps} />
              <p className="lc-hint">
                Cette connexion Meta (Business Discovery) est PARTAGÉE avec la
                veille concurrentielle et reste INDÉPENDANTE de la connexion
                propriétaire ci-dessus : déconnecter l’une n’affecte pas
                l’autre. Le jeton de veille se renouvelle depuis{' '}
                <Link href="/settings/soirees/instagram">Réglages → Soirées → Instagram</Link>.
              </p>
            </div>
          </section>

          <ClubAccountPanel club={diag.club_account} onSaved={() => { charger(); setVersionOwner((v) => v + 1) }} />

          <section className="lc-panel">
            <header className="lc-panel__head"><h2 className="lc-section-title">Ce que le compte permet aujourd’hui</h2></header>
            <div className="lc-panel__body">
              <MatriceCapacites items={diag.capabilities} />
              <p className="lc-hint">
                « Permission manquante » n’est pas une panne : Meta exige une validation
                (App Review) pour les statistiques et la publication. Gestion les activera
                dès qu’elles seront accordées — rien à changer ici.
              </p>
            </div>
          </section>

          <LiensDocs docs={diag.docs} verifiedOn={diag.docs_verified_on} />
        </>
      ) : !erreur ? <p className="lc-hint">Diagnostic en cours — un appel réel est fait au fournisseur…</p> : null}
    </main>
  )
}

export function CommInstagramIntegrationPage() {
  const meta = useShellMeta()
  return (
    <Shell active="communication" meta={meta}>
      <ToastProvider><InstagramIntegrationContent /></ToastProvider>
    </Shell>
  )
}

/* ═══════════════════ /communication/integrations/google ════════════════ */

interface GoogleDiag {
  state: string
  client_id: string | null
  account_label: string | null
  location_label: string | null
  last_sync_at: string | null
  last_error: string | null
  docs: Record<string, string>
  docs_verified_on: string
  redirect_uri: string
  steps: DiagnosticStep[]
}

function CredentialsForm({ onSaved }: { onSaved: () => void }) {
  const toast = useToast()
  const [clientId, setClientId] = useState('')
  const [clientSecret, setClientSecret] = useState('')
  const [busy, setBusy] = useState(false)
  const permissions = usePermissions()
  if (!permissions.permissions.includes('communication.settings.manage')) {
    return <p className="lc-hint">La saisie des identifiants demande le droit « gérer les réglages communication ».</p>
  }
  return (
    <div className="cm-credform">
      <Field label="Client ID OAuth" helper="Créé dans la console Google Cloud (type « application Web »).">
        <TextInput value={clientId} onChange={setClientId} placeholder="xxxxxxxx.apps.googleusercontent.com" />
      </Field>
      <Field label="Client secret" helper="Posé au coffre chiffré de Gestion — jamais relu, jamais journalisé.">
        <input className="lc-input" type="password" value={clientSecret} autoComplete="new-password"
          name="google_client_secret" onChange={(e) => setClientSecret(e.target.value)} aria-label="Client secret" />
      </Field>
      <button type="button" className="lc-btn lc-btn--primary" disabled={busy || !clientId.trim() || !clientSecret.trim()}
        onClick={async () => {
          setBusy(true)
          const r = await adminMutate<{ error?: string; message?: string }>(
            'PUT', '/api/v1/google/business/credentials',
            { client_id: clientId.trim(), client_secret: clientSecret.trim() })
          setBusy(false)
          if (r.status === 200) { toast('ok', 'Identifiants enregistrés au coffre.'); setClientSecret(''); onSaved() }
          else toast('error', r.data.message ?? 'Enregistrement impossible.')
        }}>
        Enregistrer au coffre
      </button>
    </div>
  )
}

function ChoixEtablissement({ onDone }: { onDone: () => void }) {
  const toast = useToast()
  const [comptes, setComptes] = useState<{ name: string; accountName?: string }[] | null>(null)
  const [lieux, setLieux] = useState<{ name: string; title?: string }[] | null>(null)
  const [compte, setCompte] = useState<string>('')
  useEffect(() => {
    void apiGet<{ accounts?: { name: string; accountName?: string }[] }>('/api/v1/google/business/accounts')
      .then((j) => setComptes(j.accounts ?? []))
      .catch(() => setComptes([]))
  }, [])
  useEffect(() => {
    if (!compte) return
    setLieux(null)
    void apiGet<{ locations?: { name: string; title?: string }[] }>(
      `/api/v1/google/business/locations?account=${encodeURIComponent(compte)}`)
      .then((j) => setLieux(j.locations ?? []))
      .catch(() => setLieux([]))
  }, [compte])

  if (comptes === null) return <p className="lc-hint">Lecture des comptes Google…</p>
  if (comptes.length === 0) {
    // OAUTH RÉUSSI ≠ API GBP APPROUVÉE : tant que Google examine la demande
    // d'accès à l'API Business Profile, la connexion reste valide — c'est la
    // découverte des établissements qui attend. On le dit tel quel.
    return (
      <p className="lc-hint">
        Aucun établissement lisible pour l’instant — si la demande d’accès à
        l’API Business Profile est encore en examen chez Google, c’est normal :
        la connexion reste valide, cette étape se débloquera avec l’approbation.
      </p>
    )
  }
  return (
    <div className="cm-credform">
      <Field label="Compte" helper="Le compte Business Profile qui possède la fiche.">
        <select className="lc-input" value={compte} onChange={(e) => setCompte(e.target.value)} aria-label="Compte Google">
          <option value="">— choisir —</option>
          {comptes.map((c) => <option key={c.name} value={c.name}>{c.accountName ?? c.name}</option>)}
        </select>
      </Field>
      {compte && lieux === null ? <p className="lc-hint">Lecture des établissements…</p> : null}
      {lieux && lieux.length === 0 ? <p className="lc-hint">Aucun établissement sur ce compte.</p> : null}
      {lieux && lieux.length > 0 ? (
        <ul className="cm-loclist">
          {lieux.map((l) => (
            <li key={l.name}>
              <span>{l.title ?? l.name}</span>
              <button type="button" className="lc-btn" onClick={async () => {
                const c = comptes.find((x) => x.name === compte)
                const r = await adminMutate<{ error?: string; message?: string }>(
                  'PUT', '/api/v1/google/business/location', {
                    account_name: compte, account_label: c?.accountName ?? compte,
                    location_name: l.name, location_label: l.title ?? l.name,
                  })
                if (r.status === 200) { toast('ok', 'Établissement sélectionné.'); onDone() }
                else toast('error', r.data.message ?? 'Sélection impossible.')
              }}>Choisir</button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  )
}

type RetoursOAuth = Record<string, { ok: boolean; texte: string }>

/** Messages du retour OAuth Google — un par cause RÉELLE renvoyée par le serveur. */
const OAUTH_RETOURS: RetoursOAuth = {
  ok: { ok: true, texte: 'Compte Google connecté — le consentement a été finalisé.' },
  refuse: { ok: false, texte: 'Connexion annulée ou refusée chez Google — rien n’a été modifié.' },
  STATE_INVALIDE: { ok: false, texte: 'La connexion Google n’a pas pu être finalisée : état de sécurité inconnu ou expiré. Relancez la connexion.' },
  IDENTIFIANTS_ABSENTS: { ok: false, texte: 'La connexion Google n’a pas pu être finalisée : identifiants OAuth absents du coffre.' },
  ECHANGE_REFUSE: { ok: false, texte: 'La connexion Google n’a pas pu être finalisée : Google a refusé l’échange du code. Vérifiez l’URI de redirection et le client secret.' },
  REFRESH_ABSENT: { ok: false, texte: 'Google n’a pas fourni d’accès permanent (refresh token). Retirez l’accès « Gestion » dans myaccount.google.com → Sécurité, puis reconnectez.' },
}

/** Retour OAuth Instagram (connexion propriétaire) : mêmes paramètres
 *  ?oauth=… que Google, mais un texte qui nomme le BON fournisseur — au
 *  retour d'Instagram, « Compte Google connecté » faisait douter de ce qui
 *  venait réellement d'être autorisé. */
const OAUTH_RETOURS_INSTAGRAM: RetoursOAuth = {
  ok: { ok: true, texte: 'Compte Instagram connecté — l’autorisation a été finalisée.' },
  refuse: { ok: false, texte: 'Connexion annulée ou refusée chez Instagram — rien n’a été modifié.' },
  STATE_INVALIDE: { ok: false, texte: 'La connexion Instagram n’a pas pu être finalisée : état de sécurité inconnu ou expiré. Relancez la connexion.' },
  IDENTIFIANTS_ABSENTS: { ok: false, texte: 'La connexion Instagram n’a pas pu être finalisée : identifiants de l’application Instagram absents du coffre.' },
  ECHANGE_REFUSE: { ok: false, texte: 'La connexion Instagram n’a pas pu être finalisée : Instagram a refusé l’échange du code. Relancez la connexion ; si cela se répète, vérifiez l’URL de redirection déclarée chez Meta.' },
  LONG_LIVED_REFUSE: { ok: false, texte: 'La connexion Instagram n’a pas pu être finalisée : le jeton longue durée a été refusé par Meta.' },
  COMPTE_ILLISIBLE: { ok: false, texte: 'La connexion Instagram n’a pas pu être finalisée : le compte autorisé n’a pas pu être identifié.' },
  mismatch: { ok: false, texte: 'Le compte Instagram autorisé n’est pas celui attendu — choisissez ci-dessous quoi en faire.' },
}

/** Lit puis EFFACE le paramètre ?oauth=… : le verdict s'affiche une fois,
 *  il ne doit pas ressusciter à chaque navigation interne. */
function verdictOAuthDepuisUrl(retours: RetoursOAuth): { ok: boolean; texte: string } | null {
  if (typeof window === 'undefined') return null
  const qp = new URLSearchParams(window.location.search)
  const brut = qp.get('oauth')
  if (!brut) return null
  const cause = qp.get('cause')
  qp.delete('oauth'); qp.delete('cause')
  const reste = qp.toString()
  window.history.replaceState(null, '', window.location.pathname + (reste ? `?${reste}` : ''))
  return retours[brut === 'erreur' ? (cause ?? 'erreur') : brut]
    ?? { ok: false, texte: 'La connexion n’a pas pu être finalisée.' }
}

function GoogleIntegrationContent() {
  const toast = useToast()
  const [diag, setDiag] = useState<GoogleDiag | null>(null)
  const [erreur, setErreur] = useState<string | null>(null)
  const [verdictOAuth] = useState(() => verdictOAuthDepuisUrl(OAUTH_RETOURS))
  const permissions = usePermissions()
  const peutGerer = permissions.permissions.includes('communication.settings.manage')
  const charger = () => {
    void apiGet<GoogleDiag | { error: string; message?: string }>(
      '/api/v1/communication/integrations/google/diagnostic')
      .then((j) => { if (estErreurApi(j)) setErreur(j.message ?? 'Lecture impossible.'); else { setDiag(j); setErreur(null) } })
      .catch(() => setErreur('Gestion n’a pas répondu.'))
  }
  useEffect(charger, [])

  return (
    <main className="lc-page lc-stagger cm-page">
      <Link className="lc-crumb" href="/settings/integrations">← Intégrations</Link>
      <header className="cm-head">
        <div>
          <h1 className="lc-title">Google Business Profile</h1>
          <p className="lc-subtitle">La fiche du club sur Maps et dans la recherche — visibilité, avis, publications.</p>
        </div>
        <div className="cm-head__actions">
          {diag?.state === 'CONNECTED' ? <BoutonSync provider="google-business" onDone={charger} /> : null}
        </div>
      </header>

      {verdictOAuth ? (
        <p className={verdictOAuth.ok ? 'cm-safenote cm-safenote--ok' : 'cm-safenote'} role="status">
          {verdictOAuth.texte}
        </p>
      ) : null}
      {erreur ? <p className="lc-hint">{erreur}</p> : null}
      {diag ? (
        <>
          <section className="lc-panel">
            <header className="lc-panel__head"><h2 className="lc-section-title">Où en est la connexion</h2></header>
            <div className="lc-panel__body">
              <Stepper steps={diag.steps} />
              {diag.last_error ? <p className="lc-hint">Dernière erreur relevée : {diag.last_error}</p> : null}
            </div>
          </section>

          {diag.state === 'NOT_CONFIGURED' || diag.client_id === null ? (
            <section className="lc-panel">
              <header className="lc-panel__head"><h2 className="lc-section-title">1 · Identifiants OAuth</h2></header>
              <div className="lc-panel__body">
                <CopyField label="URI de redirection à déclarer chez Google" value={diag.redirect_uri}
                  helper="Valeur canonique fournie par le serveur — à coller telle quelle dans la console Google Cloud." />
                <CredentialsForm onSaved={charger} />
              </div>
            </section>
          ) : null}

          {diag.state === 'NEEDS_AUTH' && peutGerer ? (
            <section className="lc-panel">
              <header className="lc-panel__head"><h2 className="lc-section-title">2 · Autoriser Gestion</h2></header>
              <div className="lc-panel__body">
                <p className="lc-hint">
                  La connexion s’ouvre chez Google : c’est vous qui vous identifiez, Gestion
                  ne voit jamais votre mot de passe — seulement l’autorisation accordée.
                </p>
                <button type="button" className="lc-btn lc-btn--primary" onClick={async () => {
                  const r = await adminMutate<{ error?: string; url?: string; message?: string }>(
                    'POST', '/api/v1/google/business/oauth/start')
                  if (r.status === 200 && r.data.url) window.location.href = r.data.url
                  else toast('error', r.data.message ?? 'Impossible de démarrer la connexion.')
                }}>
                  Se connecter avec Google <ExternalLink size={14} aria-hidden="true" />
                </button>
              </div>
            </section>
          ) : null}

          {diag.state === 'CONNECTED' && diag.location_label === null ? (
            <section className="lc-panel">
              <header className="lc-panel__head"><h2 className="lc-section-title">3 · Choisir l’établissement</h2></header>
              <div className="lc-panel__body"><ChoixEtablissement onDone={charger} /></div>
            </section>
          ) : null}

          <LiensDocs docs={diag.docs} verifiedOn={diag.docs_verified_on} />
        </>
      ) : !erreur ? <p className="lc-hint">Diagnostic en cours…</p> : null}
    </main>
  )
}

export function CommGoogleIntegrationPage() {
  const meta = useShellMeta()
  return (
    <Shell active="communication" meta={meta}>
      <ToastProvider><GoogleIntegrationContent /></ToastProvider>
    </Shell>
  )
}
